import { CookieBanner, useResize } from '@fuuze/shared';
import { NovuProvider } from '@novu/react';
import { type PropsWithChildren, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import type { FuuzeState } from '../components/fuuze-link';
import { NavBar } from '../components/LeftNav/nav-bar';
import { PlayerCollapsed } from '../components/Player/player-collapsed';
import { useUser } from '../Hooks/use-user.hook';
import { usePlayerStore } from '../store/player.store';
import { Toast } from '../utils/toast.util';
import styles from './page-container.module.css';

const PLAYER_HIDDEN_PATHS = [/\/creator\/tiers\/.*/];

export default function PageContainer() {
  const location = useLocation();
  const [playerCollapsedHeight, podcastPlaying, loadAudio] = usePlayerStore(
    useShallow(state => [state.playerCollapsedHeight, state.podcastPlaying, state.loadAudio]),
  );
  const { t } = useTranslation();
  const hidePlayer = useMemo(() => {
    const isPodcastPlaying = !!podcastPlaying;
    const shouldHideThePlayerBasedOnLocationState = (location.state as FuuzeState)?.hidePlayer;
    const shouldHideThePlayerBasedOnLocationPath = PLAYER_HIDDEN_PATHS.some(path => location.pathname.match(path));
    return !isPodcastPlaying || shouldHideThePlayerBasedOnLocationPath || shouldHideThePlayerBasedOnLocationState;
  }, [location, podcastPlaying]);

  useEffect(() => {
    const runLoadAudio = async () => {
      if (podcastPlaying) {
        await loadAudio(podcastPlaying, { play: false });
      }
    };
    runLoadAudio();
  }, []);

  return (
    <NovuWrapper>
      <div className={styles.wrapper} data-player-shown={!hidePlayer}>
        <div className={styles.navbar}>
          <NavBar />
        </div>
        {!hidePlayer && (
          <div className={styles.player}>
            <PlayerCollapsed podcastPlaying={podcastPlaying!} />
          </div>
        )}
        <div className={styles.outlet}>
          <Outlet />
        </div>
      </div>

      <Toast.FuuzeToastContainer
        playerHeight={playerCollapsedHeight}
        limit={3}
        newestOnTop
        closeOnClick
        draggable={false}
      />
      <CookieBanner
        title={t('cookieBanner.title')}
        declineText={t('cookieBanner.decline')}
        acceptText={t('cookieBanner.accept')}
      />
    </NovuWrapper>
  );
}

function NovuWrapper(props: PropsWithChildren) {
  const user = useUser();

  if (user.data) {
    return (
      <NovuProvider
        subscriber={user.data?._id}
        applicationIdentifier={import.meta.env.VITE_NOVU_APPLICATION_IDENTIFIER}
      >
        {props.children}
      </NovuProvider>
    );
  }

  return props.children;
}
